import { useGetAnswerSubCategory } from "@/api/app/assessment/useGetAnswerSubCategory";
import LoadingScreen from "@/components/Loading";
import {
  AnswerSubCategoryDetail,
  answerSubCategoryDetailSchema,
} from "@/schema/app/assessment/answerSubCategoryDetail";
import AppContentContainer from "@/src/app/(auth)/_components/AppContentContainer";
import Heading from "@/src/app/(auth)/assessment/categories/[id]/sub-categories/[sid]/_components/Heading";
import SubCategory from "@/src/app/(auth)/assessment/categories/[id]/sub-categories/[sid]/_components/SubCategory/SubCategory";
import SubCategoryBottomBar from "@/src/app/(auth)/assessment/categories/[id]/sub-categories/[sid]/_components/SubCategoryBottomBar";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useEffect, useRef } from "react";

function AssessmentSubCategory() {
  const { categoryId, subCategoryId = "" } = useParams();
  const {
    data,
    mutate: refetch,
    isLoading,
  } = useGetAnswerSubCategory(subCategoryId, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
  });

  const methods = useForm<AnswerSubCategoryDetail>({
    resolver: zodResolver(answerSubCategoryDetailSchema),
    defaultValues: data?.data?.data,
  });

  const lastResetIdRef = useRef<string | null>(null);
  const lastDataSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const incoming = data?.data?.data;
    if (!incoming) {
      return;
    }
    const dataSignature = JSON.stringify(incoming.answer_contents);
    if (lastResetIdRef.current !== subCategoryId) {
      methods.reset(incoming);
      lastResetIdRef.current = subCategoryId;
      lastDataSignatureRef.current = dataSignature;
      return;
    }

    if (!methods.formState.isDirty && lastDataSignatureRef.current !== dataSignature) {
      methods.reset(incoming);
      lastDataSignatureRef.current = dataSignature;
    }
  }, [data?.data?.data, methods, subCategoryId]);

  if (isLoading || !data) {
    return <LoadingScreen />;
  }

  const { data: subCategory, prev, next } = data.data;
  const questionsCount = subCategory.answer_contents.length;

  return (
    <>
      <FormProvider {...methods}>
        <AppContentContainer
          bottomBar={
            <SubCategoryBottomBar
              backLink={`/app/assessment/categories/${categoryId}`}
              previousLink={
                prev
                  ? `/app/assessment/categories/${categoryId}/sub-categories/${prev}`
                  : ""
              }
              nextLink={
                next
                  ? `/app/assessment/categories/${categoryId}/sub-categories/${next}`
                  : ""
              }
              refetch={refetch}
              subCategoryId={subCategoryId}
            />
          }
        >
          <div className="px-32 pt-4 pb-8">
            <div className="mt-8 border-b pb-5">
              <div className="flex items-start justify-between gap-2">
                <Heading
                  content={subCategory.content}
                  questionsCount={questionsCount}
                />
              </div>
            </div>
            {/* content */}
            <div className="mt-8">
              <SubCategory />
            </div>
          </div>
        </AppContentContainer>
      </FormProvider>
    </>
  );
}

export default AssessmentSubCategory;